import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Collections as CollectionsIcon,
  Sell as SellIcon,
  ViewModule as SetsIcon,
  Star as GradingIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Radar as RadarIcon,
  CompareArrows as CompareArrowsIcon,
  Inventory as InventoryIcon,
  Policy as PolicyIcon,
  Gavel as GavelIcon,
  ShoppingCart as ShoppingCartIcon,
  AutoAwesome as PackIcon,
  Storefront as StoreIcon,
  AdminPanelSettings as AdminIcon,
  Event as EventIcon,
  EmojiEvents as TrophyIcon,
  ContactMail as ContactMailIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import ImmersiveBackground from '../ImmersiveBackground';
import { alpha } from '@mui/material';
import { usePagePersistence } from '../../contexts/PagePersistenceContext';
import AuthDialog from '../AuthDialog';
import MainTabSwitcher from './MainTabSwitcher';

const drawerWidth = 260;

interface LayoutProps {
  children?: React.ReactNode;
}

interface NavItem {
  text: string;
  icon: any;
  path?: string;
  subItems?: NavItem[];
}

const navigationGroups: { title?: string; items: NavItem[] }[] = [
  {
    items: [
      { text: 'Search', icon: SearchIcon, path: '/search' },
    ]
  },
  {
    title: "Collector's Hub",
    items: [
      { 
        text: 'Collection', 
        icon: CollectionsIcon, 
        subItems: [
          { text: 'My Collection', icon: CollectionsIcon, path: '/collection' },
          { text: 'Trades', icon: CompareArrowsIcon, path: '/trades/history' },
          { text: 'Buy List', icon: ShoppingCartIcon, path: '/buy-list' },
          { text: 'Grading', icon: GradingIcon, path: '/grading' },
          { text: 'Sold Inventory', icon: SellIcon, path: '/sold' },
        ]
      },
      { text: 'Master Sets', icon: SetsIcon, path: '/master-sets' },
      { text: 'Deck Builder', icon: DashboardIcon, path: '/onepiece/deck-builder' },
      { text: 'Pack Opening', icon: PackIcon, path: '/packs' },
      { text: 'TCG Drops', icon: InventoryIcon, path: '/drops' },
    ]
  },
  {
    title: "Shop & Play",
    items: [
      { text: 'Public Store', icon: StoreIcon, path: '/store' },
      { 
        text: 'Events & Tourneys', 
        icon: EventIcon, 
        subItems: [
          { text: 'Events', icon: EventIcon, path: '/events' },
          { text: 'Tournaments', icon: TrophyIcon, path: '/tournaments' },
        ]
      },
      { text: 'Contact Us', icon: ContactMailIcon, path: '/contact' },
    ]
  }
];

const adminItems = [
  { text: 'Store Admin', icon: AdminIcon, path: '/admin/store', adminOnly: true },
];

const navigationItems = [
  ...navigationGroups.flatMap(group => 
    group.items.flatMap(item => item.subItems ? [item, ...item.subItems] : [item])
  ),
  ...adminItems
];

const legalItems = [
  { text: 'Privacy Policy', icon: PolicyIcon, path: '/privacy' },
  { text: 'Terms of Service', icon: GavelIcon, path: '/tos' },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { mode, toggleTheme } = useThemeContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, signOut, isAuthModalOpen, setAuthModalOpen } = useAuth();
  const { getLastVisitedPath, setLastVisitedPath } = usePagePersistence();

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    "Collection": false,
    "Events & Tourneys": false,
  });

  const toggleGroup = (title: string) => {
    setOpenGroups((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const isStaff = React.useMemo(() => {
    if (!isAuthenticated || !user) return false;
    const userGroups = user.groups || [];
    return userGroups.includes('Owners') || 
           userGroups.includes('SuperAdmins') || 
           userGroups.includes('Admins') || 
           userGroups.includes('Employees');
  }, [isAuthenticated, user]);

  const regularGroups = React.useMemo(() => {
    return navigationGroups;
  }, []);

  const managementItems = React.useMemo(() => {
    return adminItems;
  }, []);

  // Track last visited paths for categories
  React.useEffect(() => {
    // 1. Check direct matches from navigationItems
    for (const item of navigationItems) {
      if (item.path && location.pathname.startsWith(item.path)) {
        setLastVisitedPath(item.path, location.pathname);
        return;
      }
    }

    // 2. Check aliases for Master Sets
    if (location.pathname.startsWith('/sets') || location.pathname.startsWith('/onepiece/sets')) {
      setLastVisitedPath('/master-sets', location.pathname);
    }
  }, [location.pathname, setLastVisitedPath]);

  const isTabRoute = React.useMemo(() => {
    const tabRoots = [
      '/dashboard',
      '/search',
      '/collection',
      '/packs',
      '/sold',
      '/drops',
      '/onepiece/deck-builder',
      '/master-sets',
      '/sets',
      '/onepiece/sets',
      '/grading',
      '/trades/history'
    ];
    
    // If it's a detail page (more than 2 segments or contains '/cards/'), it's NOT a tab route
    // Example: /search (tab), /search/set/card (detail)
    const segments = location.pathname.split('/').filter(Boolean);
    const isDetail = (segments.length > 2 && (segments[0] === 'search' || segments[0] === 'collection')) ||
                     location.pathname.includes('/cards/');
    
    if (isDetail) return false;

    return tabRoots.some(root => location.pathname === root || location.pathname.startsWith(root + '/'));
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    // Logic for persistent navigation
    const savedPath = getLastVisitedPath(path);
    
    if (savedPath && savedPath !== location.pathname) {
      // If we have a saved path and we're not already there, go to it
      navigate(savedPath);
    } else {
      // Otherwise go to the default root path
      navigate(path);
    }

    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar>
        <Typography 
          variant="h6" 
          color="text.primary" 
          fontWeight="bold"
          onClick={() => handleNavigation('/')}
          sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
        >
          Chasing TCG
        </Typography>
      </Toolbar>
      <List sx={{ pt: 0 }}>
        {regularGroups.map((group, index) => (
          <React.Fragment key={group.title || `group-${index}`}>
            {index > 0 && group.title && <Divider sx={{ mx: 2, my: 1, opacity: 0.5 }} />}
            {group.title && (
              <Typography
                variant="caption"
                sx={{
                  px: 3,
                  display: 'block',
                  color: theme.palette.text.secondary,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  mb: 0.5,
                  mt: index === 0 ? 1 : 0
                }}
              >
                {group.title}
              </Typography>
            )}
            
            {group.items.map((item) => {
              if (item.subItems) {
                const Icon = item.icon;
                return (
                  <React.Fragment key={item.text}>
                    <ListItem disablePadding>
                      <ListItemButton 
                        onClick={() => toggleGroup(item.text)}
                        sx={{
                          mx: 1,
                          borderRadius: 1.5,
                          mb: 0.5,
                        }}
                      >
                        <ListItemIcon sx={{ color: 'inherit' }}>
                          <Icon />
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.text} 
                          primaryTypographyProps={{ fontWeight: 400 }} 
                        />
                        {openGroups[item.text] ? <ExpandLess sx={{ color: 'text.secondary', fontSize: 18 }} /> : <ExpandMore sx={{ color: 'text.secondary', fontSize: 18 }} />}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={openGroups[item.text]} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.subItems.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isActive = location.pathname === subItem.path;
                          return (
                            <ListItem key={subItem.text} disablePadding>
                              <ListItemButton
                                onClick={() => subItem.path && handleNavigation(subItem.path)}
                                selected={isActive}
                                sx={{
                                  mx: 1,
                                  pl: 4,
                                  borderRadius: 1.5,
                                  mb: 0.5,
                                  '&.Mui-selected': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                    color: theme.palette.primary.main,
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.primary.main, 0.18),
                                    },
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ color: isActive ? theme.palette.primary.main : 'inherit' }}>
                                  <SubIcon />
                                </ListItemIcon>
                                <ListItemText
                                  primary={subItem.text}
                                  primaryTypographyProps={{ fontWeight: isActive ? 600 : 400 }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              }

              // Normal non-collapsible item
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => item.path && handleNavigation(item.path)}
                    selected={isActive}
                    sx={{
                      mx: 1,
                      borderRadius: 1.5,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.18),
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive
                          ? theme.palette.primary.main
                          : 'inherit',
                      }}
                    >
                      <Icon />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </React.Fragment>
        ))}
      </List>
      
      <Box sx={{ flexGrow: 1 }} />
      
      {isStaff && (
        <>
          <Divider sx={{ mx: 2, mb: 1, opacity: 0.5 }} />
          <Typography
            variant="caption"
            sx={{
              px: 3,
              display: 'block',
              color: theme.palette.primary.main,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 1,
              mb: 0.5,
            }}
          >
            Management
          </Typography>
          <List sx={{ pt: 0, pb: 0 }}>
            {managementItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={isActive}
                    sx={{
                      mx: 1,
                      borderRadius: 1.5,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.18),
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive
                          ? theme.palette.primary.main
                          : 'inherit',
                      }}
                    >
                      <Icon />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </>
      )}
      
      <Divider sx={{ mx: 2, mb: 1, opacity: 0.5 }} />
      
      <List sx={{ mb: 2 }}>
        {legalItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;

          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={isActive}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  mb: 0.5,
                  py: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: isActive ? theme.palette.primary.main : 'text.secondary',
                  }}
                >
                  <Icon sx={{ fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: 'text.secondary',
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <ImmersiveBackground />
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
                flexGrow: 1, 
                cursor: 'pointer',
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 0.8 }
            }}
            onClick={() => handleNavigation('/')}
          >
            {navigationItems.find((item) => item.path === location.pathname)
              ?.text || 'Chasing TCG'}
          </Typography>

          {/* Theme Toggle */}
          <IconButton onClick={toggleTheme} color="inherit" sx={{ mr: 2 }}>
            {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>

          {/* Auth Controls */}
          {isAuthenticated ? (
            <>
              <IconButton
                color="inherit"
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                <Avatar
                  sx={{ width: 32, height: 32, bgcolor: theme.palette.secondary.main }}
                >
                  {user?.email?.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                <MenuItem disabled>
                  <Typography variant="body2">{user?.email}</Typography>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => navigate('/settings')}>
                  <ListItemIcon>
                    <AccountIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Settings</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    signOut();
                    setAnchorEl(null);
                    navigate('/');
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Sign Out</ListItemText>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button color="inherit" onClick={() => setAuthModalOpen(true)}>
              Sign In
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: '64px', // Height of AppBar
        }}
      >
        <Box sx={{ display: isTabRoute ? 'block' : 'none' }}>
          <MainTabSwitcher />
        </Box>
        {!isTabRoute && <Outlet />}
      </Box>

      {/* Auth Dialog */}
      <AuthDialog
        open={isAuthModalOpen}
        onClose={() => setAuthModalOpen(false)}
      />
    </Box>
  );
};

export default Layout;
